
import { env } from '@/config/env';
import { IHttpClient, RequestConfig } from './http-client.interface';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { oauth2Service } from '@/features/auth/infrastructure/api/oauth2.service';
import { cookieService } from '@/shared/infrastructure/storage/cookie.service';


// Helper function to extract data from ResponseUtil format
function extractResponseData<T>(responseData: any): T {
  if (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData) {
    return responseData.data;
  }
  return responseData;
}

export class HttpClient implements IHttpClient {
  private axiosInstance: AxiosInstance;

  constructor(timeout: number = 30000) {
    // Usar API_URL_DOCUMENTOS como baseURL por defecto (para manifiestos y diccionarios)
    this.axiosInstance = axios.create({
      baseURL: `${env.API_URL_DOCUMENTOS}`,
      timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Flag para evitar múltiples refreshes simultáneos
    let isRefreshing = false;
    let failedQueue: Array<{
      resolve: (value?: unknown) => void;
      reject: (reason?: any) => void;
    }> = [];

    const processQueue = (error: any, token: string | null = null) => {
      failedQueue.forEach((prom) => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve(token);
        }
      });
      failedQueue = [];
    };

    // Request Interceptor
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        let tokens = oauth2Service.getTokens();
        
        // Si no hay tokens pero hay refresh token en cookie, intentar restaurar sesión
        if (!tokens) {
          const refreshToken = cookieService.get('oauth2_refresh_token');
          if (refreshToken && !isRefreshing) {
            try {
              tokens = await oauth2Service.restoreSession();
            } catch (error) {
              console.error('Error al restaurar sesión:', error);
            }
          }
        }
        
        if (tokens) {
          // Verificar si el token está expirado o por expirar
          if (oauth2Service.isTokenExpired(tokens)) {
            // Intentar refrescar si hay refresh token
            if (tokens.refresh_token && !isRefreshing) {
              isRefreshing = true;
              try {
                const newTokens = await oauth2Service.refreshToken(tokens.refresh_token);
                oauth2Service.saveTokens(newTokens);
                if (config.headers) {
                  config.headers.Authorization = `Bearer ${newTokens.access_token}`;
                }
                processQueue(null, newTokens.access_token);
                isRefreshing = false;
                return config;
              } catch (refreshError) {
                processQueue(refreshError, null);
                oauth2Service.clearTokens();
                isRefreshing = false;
                // Redirigir al login si falla el refresh
                window.location.href = '/login';
                return Promise.reject(refreshError);
              }
            } else if (isRefreshing) {
              // Si ya se está refrescando, esperar
              return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
              }).then((token) => {
                if (token && config.headers) {
                  config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
              });
            } else {
              // No hay refresh token, limpiar y redirigir
              oauth2Service.clearTokens();
              window.location.href = '/login';
            }
          } else {
            // Token válido, usar directamente
            if (config.headers) {
              config.headers.Authorization = `Bearer ${tokens.access_token}`;
            }
          }
        }

        // Mantener compatibilidad con auth_token legacy (migración gradual)
        const legacyToken = localStorage.getItem('auth_token');
        if (legacyToken && !tokens && config.headers) {
          config.headers.Authorization = `Bearer ${legacyToken}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response Interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Manejar errores del ResponseUtil
        if (error.response?.data && typeof error.response.data === 'object') {
          const responseData = error.response.data as any;
          if (responseData.success === false && responseData.error) {
            // Crear un error más descriptivo con el mensaje del ResponseUtil
            const customError = new Error(responseData.error);
            (customError as any).response = error.response;
            (customError as any).status = error.response.status;
            return Promise.reject(customError);
          }
        }

        // Manejar 401 Unauthorized
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          const tokens = oauth2Service.getTokens();

          if (tokens?.refresh_token && !isRefreshing) {
            originalRequest._retry = true;
            isRefreshing = true;

            try {
              const newTokens = await oauth2Service.refreshToken(tokens.refresh_token);
              oauth2Service.saveTokens(newTokens);

              // Actualizar el header del request original
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newTokens.access_token}`;
              }

              processQueue(null, newTokens.access_token);
              isRefreshing = false;

              // Reintentar el request original con el nuevo token
              return this.axiosInstance(originalRequest);
            } catch (refreshError) {
              processQueue(refreshError, null);
              oauth2Service.clearTokens();
              isRefreshing = false;
              
              // Eliminar también token legacy si existe
              localStorage.removeItem('auth_token');
              window.location.href = '/login';
              return Promise.reject(refreshError);
            }
          } else if (isRefreshing) {
            // Si ya se está refrescando, esperar y reintentar
            return new Promise((resolve, reject) => {
              failedQueue.push({
                resolve: (token) => {
                  if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                  }
                  resolve(this.axiosInstance(originalRequest));
                },
                reject,
              });
            });
          } else {
            // No se puede refrescar, limpiar y redirigir
            oauth2Service.clearTokens();
            localStorage.removeItem('auth_token');
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private mapConfig(config?: RequestConfig): AxiosRequestConfig {
    return {
      headers: config?.headers,
      params: config?.params,
      timeout: config?.timeout,
      signal: config?.signal,
    };
  }

  async get<T>(url: string, config?: RequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, this.mapConfig(config));
    return extractResponseData<T>(response.data);
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, this.mapConfig(config));
    return extractResponseData<T>(response.data);
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data, this.mapConfig(config));
    return extractResponseData<T>(response.data);
  }

  async delete<T>(url: string, config?: RequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url, this.mapConfig(config));
    return extractResponseData<T>(response.data);
  }

  async patch<T>(
    url: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.patch<T>(url, data, this.mapConfig(config));
    return extractResponseData<T>(response.data);
  }

  async postBlob(
    url: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<Blob> {
    const response = await this.axiosInstance.post<Blob>(
      url,
      data,
      {
        ...this.mapConfig(config),
        responseType: 'blob',
      }
    );
    return response.data;
  }
}

// Singleton instance
export const httpClient = new HttpClient();

